<template>
  <div class="percent-wrap">
    <div class="label-top" aria-hidden="true">
      <div class="label-drag">
        <i class="mdi mdi-arrow-left"></i>
        <span>{{ labelClick + labels.andDrag }}</span>
        <i class="mdi mdi-arrow-right"></i>
      </div>
    </div>

    <div class="percent">
      <div class="bar" aria-hidden="true">
        <div class="label"></div>
        <div class="track" :style="trackState">
          <div class="pipe"></div>
          <div class="pipe"></div>
          <div class="pipe"></div>
        </div>
        <div
          class="track check mdi mdi-check-circle"
          :style="trackState"
          v-if="answered.answer_id && !mouseDown"
        ></div>
      </div>

      <input
        ref="percent-slider"
        role="slider"
        type="range"
        tabindex="4"
        :aria-valuetext="currentValueHidden"
        :value="currentValue"
        @keydown.right="changeToRight"
        @keydown.left="changeToLeft"
        @keydown.enter="focusSlider"
        @change="inputChange"
        @input="inputChange"
        @mousedown="mouseDown = true"
        @mouseup="mouseDown = false"
      />
    </div>

    <div class="answers" aria-hidden="true">
      <div
        class="answer"
        :class="{ 'answer-ie-11': answer.text.trim().length > 0 }"
        v-for="(answer, index) in answers"
        :key="index"
      >
        <div class="pipe" v-if="answer.text.trim()"></div>
        <span>{{ answer.text }}</span>
      </div>
    </div>

    <div class="image-wrap mb-3" v-if="answered.image" aria-hidden="true">
      <img :src="answered.image" />
    </div>

    <div class="image-wrap mb-3" v-if="!answered.image" aria-hidden="true">
      <img :src="neutralImage" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const props = {
  question: {
    type: Object,
    required: true,
    validator: function(question) {
      return Array.isArray(question.answers)
    },
  },
}

function isMobile() {
  return window.innerWidth < 767
}

export default {
  props: props,
  data: function() {
    return {
      currentValue: null,
      barState: {},
      trackState: {},
      labelClick: 'click',
      currentAnswer: {},
      mouseDown: false,
    }
  },

  mounted: function() {
    this.updateSlider(this.getCurrentValue(), true)
    this.labelClick = this.labels.click
    window.onresize = this.updateLabelClick
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
    }),
    labels() {
      return this.resources.component.percent
    },
    answers: function() {
      return this.question.answers || []
    },
    answered: function() {
      return this.question.answered || {}
    },
    neutralImage: function() {
      return this.question.answers[Math.trunc(this.question.answers.length / 2)]
        .image
    },
    currentValueHidden() {
      const isAnswered = Object.keys(this.answered).length !== 0
      return [
        isAnswered ? '' : this.labels.initialValue,
        this.currentValue,
        '%',
        isAnswered ? this.labels.selected : '',
      ].join(' ')
    },
  },
  methods: {
    verifyLocalStoraState() {
      if (!localStorage.percentSlider) {
        return false
      }

      const percent = JSON.parse(localStorage.percentSlider)

      return percent[this.question.question_id] || false
    },

    setLocalStoraState(state) {
      if (!localStorage.percentSlider)
        localStorage.percentSlider = JSON.stringify({})

      const percent = JSON.parse(localStorage.percentSlider)

      percent[this.question.question_id] = state

      localStorage.percentSlider = JSON.stringify(percent)
    },

    getCurrentValue: function() {
      if (!this.answered.answer_id) return 50

      const percent = this.verifyLocalStoraState()

      if (percent) return percent

      return (
        (this.answers.findIndex(
          item => item.answer_id === this.answered.answer_id
        ) *
          100) /
        this.answers.length
      )
    },

    updateLabelClick: function() {
      this.labelClick = isMobile() ? this.labels.touch : this.labels.click
    },

    inputChange: function(event) {
      this.updateSlider(event.target.value)
    },

    changeToLeft() {
      const value = parseInt(this.currentValue) - 9
      if (value > 0) {
        this.updateSlider(value)
      } else {
        this.updateSlider(0)
      }
    },

    changeToRight() {
      const value = parseInt(this.currentValue) + 9
      if (value < 100) {
        this.updateSlider(value)
      } else {
        this.updateSlider(100)
      }
    },

    setBarState: function(value) {
      const compensation = (value * 50) / 100 - 25
      this.trackState = { left: `calc(${value}% - ${compensation}px)` }
    },

    updateSlider: function(value, isMount) {
      this.currentValue = value
      this.setBarState(value)
      this.setLocalStoraState(value)
      const rounded = Math.round((value * (this.answers.length - 1)) / 100)
      const current = this.answers[rounded]
      if (!isMount && current.answer_id) this.$emit('answer', current)
    },

    focusSlider() {
      this.$refs['percent-slider'].blur()
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

_:-ms-fullscreen,
:root .answer-ie-11 {
  display: block !important;
  width: 150px !important;
}

.percent-wrap {
  position: relative;
  width: 90%;

  .label-top {
    height: 30px;
    margin-bottom: 10px;
    position: relative;

    .label-drag {
      position: absolute;
      margin-left: -75px;
      left: 50%;

      i {
        vertical-align: middle;
      }

      span {
        font-size: 0.9rem;
        margin: 0.5rem;
      }
    }
  }

  .percent {
    background: #f6f6f6;
    border-radius: 50px;
    border: 1px solid rgba(183, 184, 187, 0.8);
    height: 60px;
    position: relative;
    width: 100%;
    order: 1;

    input[type='range']::-webkit-slider-thumb {
      -webkit-appearance: none;
      height: 70px;
      width: 70px;
      border-radius: 50%;
    }

    input[type='range'] {
      height: 100%;
      width: 100%;
      position: relative;
      opacity: 0;
    }

    .bar {
      transition: background 0.1s ease;
      align-items: center;
      bottom: 0;
      display: flex;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;

      .track {
        align-items: center;
        background: map-get($theme-colors, 'gray05');
        border-radius: 40px;
        border: 1px solid rgba(204, 204, 204, 0.5);
        box-shadow: 1px 1px 3px 2px rgba(47, 42, 42, 0.2);
        cursor: pointer;
        display: flex;
        height: 50px;
        justify-content: center;
        margin-left: -25px;
        position: absolute;
        top: 4px;
        width: 50px;

        &.check {
          font-size: 62px;
          border: solid 1px rgba(0, 168, 119, 0.5);
          color: map-get($theme-colors, 'lima');
          background-color: white;
        }

        .pipe {
          background: white;
          border-radius: 4px;
          height: 70%;
          margin: 0 2px;
          width: 5px;

          &:first-child,
          &:last-child {
            height: 55%;
          }
        }
      }
    }
  }

  .answers {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
    padding: 0 25px;
    margin-top: 10px;
    order: 3;

    @media (max-width: 768px) {
      justify-content: space-around;
    }

    .answer {
      align-items: center;
      display: flex;
      justify-content: center;
      flex-direction: column;
      width: 1px;

      .pipe {
        width: 0;
        height: 20px;
        border-left: 1px dashed map-get($theme-colors, 'gray03');
      }

      span {
        width: 140px;
      }
    }
  }

  .image-wrap {
    position: absolute;
    width: 100%;
    margin-top: -60px;

    @media (max-width: 768px) {
      margin-top: 0px;
    }

    img {
      border-radius: 10px;
      margin-top: 20px;
      max-width: 150px;
      height: auto;
    }
  }
}
</style>
